import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import * as usersService from "../service/Users"
import { UserDetailRead } from "../api/generated/models/UserDetailRead"
import useShowError from "./useShowError"
import { useAuth } from '../components/AuthContext/AuthContext'
import { UserCreateRequest } from "../api/generated/models/UserCreateRequest"
import { RolesBelowResponse, UserUpdateRequest, PasswordResetRequest} from "../api/generated"
import { QUEUE_DROPDOWN_KEY } from "../constants"

const USERS = "users"
const CURRENT_USER = "current"
const LOGIN_MODE="login_mode"
const FILTERED_USERS= "users-filtered"

export const useGetUsers = () =>
  useQuery({
    queryKey: [USERS],
    queryFn: () => usersService.getAllUsers(),
    placeholderData: [],
  })

export const useCurrentUser = () => {
  const { authToken } = useAuth();

  return useQuery({
    queryKey: [USERS, CURRENT_USER],
    queryFn: () => usersService.getCurrentUser(),
    enabled: authToken.length > 0 && localStorage.getItem("expiration")!== null,
  })
}

export const useCreateUser = ()=>{
    const queryClient = useQueryClient()
    const showError = useShowError()
    return useMutation({
      mutationFn: (userPayload:UserCreateRequest)=>usersService.createUser(userPayload),
    })
}

export const useUpdateUserTeam = () => {
  const queryClient = useQueryClient()
  const showError = useShowError()

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload:UserUpdateRequest }) =>
      usersService.updateUser(id,payload),
    onSuccess(response:UserDetailRead) {
      queryClient.setQueryData([USERS], (old: Array<UserDetailRead>) =>
        old.map((item) => (item.id === response?.id ? response : item)),
      )
      queryClient.invalidateQueries({ queryKey: QUEUE_DROPDOWN_KEY });
    },
    onError(error) {
      showError(`Failed to update team: ${error.message}`)
    },
  })
}

export const useUserLogin = () => {
  const showError = useShowError()

  return useMutation({
    mutationFn: ({ email, password }: {email: string, password: string}) =>
      usersService.userLogin({ email, password }),
    onError(error:any) {
      const detail = error?.body?.detail || error.message || "Unknown error";
      showError(`Login attempt unsuccessful: ${detail}`);
    },
  })
}

export const usePasswordlessLogin = () => {
  const showError = useShowError()
  return useMutation({
    mutationFn: ({ email,LGInternalUser }:{email: string,LGInternalUser:boolean}) =>{
      if(LGInternalUser){
        return usersService.internalUserPasswordlessLogin({ email })
      }else{
        return usersService.userPasswordlessLogin({ email })
      }
    }
      ,
    onError(error:any) {
      const detail = error?.body?.detail || error.message || "Unknown error";
      showError(`${detail}`);
    },
  })
}

export const useLoginWithTempPassword=() => {
  const showError = useShowError()
  return useMutation({
    mutationFn: ({ email, password }:{email: string, password: string}) =>
      usersService.loginWithTempPassword({ email, password }),
    onError(error:any) {
      const detail = error?.body?.detail || error.message || "Unknown error";
      showError(`${detail}`)
    },
  })
}

export const useGetLoginMode = () => useQuery({
  queryKey: [LOGIN_MODE],
  queryFn: () => usersService.getLoginMode(),
})

export const useLogoutUser = () => {
  const showError = useShowError()
  return useMutation({
    mutationFn: () =>
      usersService.userLogout(),
    onError(error) {
      showError(`Logout attempt unsuccessful: ${error.message}`)
    },
  })
}

export const useForgotPassword = () => {
  const showError = useShowError()
  return useMutation({
    mutationFn: ({ email }:{email: string}) =>
      usersService.forgotPassword({ email }),
    onError(error:any) {
      const detail = error?.body?.detail || error.message || "Unknown error";
      showError(`${detail}`)
    },
  })
}


export const useValidateForgotPassword=() => {
  const showError = useShowError()
  return useMutation({
    mutationFn: ({ email, password }:{email: string, password: string}) =>
      usersService.validateForgotPassword({ email, password }),
    onError(error:any) {
      const detail = error?.body?.detail || error.message || "Unknown error";
      showError(`${detail}`)
    },
  })
}

export const useRefreshToken = () => {
  const showError = useShowError();
  return useMutation({
    mutationFn: () => usersService.refreshToken(),
    onError(error) {
      showError(`${error.message}`)
    }
  })
}

export const useSsoToken = () => {  
  return useMutation({
    mutationFn: () => usersService.ssoTokenGet()
  })
}

export const useSsoValidateEmail = () => {
  const showError = useShowError();
  return useMutation({
  mutationFn: ({ email }:{email: string}) => usersService.ssoValidateEmailPOST({ email }),
  onError() {
    showError("Login attempt unsuccessful. Please contact the administrator for further assistance.")
  },
  })
}

export const useDownloaduserBulkUpldTemplate =()=>{
    return useMutation({
    mutationFn: () => usersService.downloadUserBulkUpldTemplate(),
  })

}

export const useUploadUserBulkUpldTemplate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => {
      return usersService.uploadUserBulkUpldTemplate({ file })
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [USERS] })
    }
  })
}

export const useGetAvailableRolesOfUser = () => {
  return useQuery<RolesBelowResponse, Error>({
    queryKey: ['subordinateRoles'],
    queryFn: () => usersService.getAvailableRolesOfUser(),
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: (payload: PasswordResetRequest) => {
      return usersService.resetPassword(payload);
    },
  })
};


export const useGetAllUsersByTeamsAndRole = () =>
  useQuery({
    queryKey: [FILTERED_USERS],
    queryFn: () => usersService.getAllFilteredUsersByTeams(),
    placeholderData: [],
  })

